import React, { useMemo, useCallback, useState, useEffect } from "react";
import { createUser, updateUser } from "../../../service/UserService";
import Form from "../../../components/shared/new Form/Fields/Form";
import { useFormValidation } from "../../../utility/validationHooks/useFormValidation";
import { PayloadBuilder } from "../../../utility/payloadBuilder";
import { getUserFormFields } from "../../../assets/FormFeilds/GetUserFormField";
import { userValidationSchema } from "./UserValidationSchema";
import { USER_FORM_SECTION_CONFIG } from "../../../assets/FormFeilds/Config/formFieldConfig";
import "./UsersModal.css";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../components/shared/pagination/messages";
import CustomModal from "../../../components/global/CustomModal/CustomModal";
import { useSelector } from "react-redux";

const UsersModal = ({
  isModalOpen,
  getUserLists,
  handleCloseModal,
  selectedUser,
}) => {
  const isEditing = Boolean(selectedUser);
  const auth = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const isCurrentUserAdminSupport = auth?.roles?.includes("ADMIN_SUPPORT");

  const [formData, setFormData] = useState(() => {
    if (isEditing) {
      return {
        firstName: selectedUser?.firstName ?? "",
        lastName: selectedUser?.lastName ?? "",
        userName: selectedUser?.employeeId ?? "",
        email: selectedUser?.email ?? "",
        role: selectedUser?.role ?? "",
      };
    }
    return isCurrentUserAdminSupport ? { role: "EMPLOYEE" } : {};
  });
  const validationSchema = useMemo(
    () => userValidationSchema(isEditing, isCurrentUserAdminSupport),
    [isEditing, isCurrentUserAdminSupport],
  );
  const { errors, validateField, validateForm, touchField, setErrors } =
    useFormValidation(validationSchema);

  useEffect(() => {
    if (isModalOpen) {
      if (isEditing) {
        setFormData({
          firstName: selectedUser?.firstName ?? "",
          lastName: selectedUser?.lastName ?? "",
          userName: selectedUser?.username ?? "",
          email: selectedUser?.email ?? "",
          role: selectedUser?.role ?? "",
        });
      } else {
        setFormData(isCurrentUserAdminSupport ? { role: "EMPLOYEE" } : {});
      }
    }
  }, [isModalOpen, selectedUser, isEditing, isCurrentUserAdminSupport]);

  const handleFormChange = useCallback(
    (name, value) => {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) {
        validateField(name, value, { ...formData, [name]: value });
      }
    },
    [errors, formData, validateField],
  );

  const handleBlur = useCallback(
    (name) => {
      touchField(name);
      validateField(name, formData[name], formData);
    },
    [formData, touchField, validateField],
  );

  const handleClose = useCallback(() => {
    setFormData({});
    setShowPassword(false);
    if (setErrors) {
      setErrors({});
    }
    handleCloseModal();
  }, [handleCloseModal, setErrors]);

  const handleSubmit = useCallback(async () => {
    const { isValid } = validateForm(formData);
    if (!isValid) {
      showErrorMessage("Please fill in all the Required Fields.");
      return;
    }
    try {
      setLoading(true);

      let payload;

      if (isEditing) {
        const fieldMap = {
          firstName: "firstName",
          lastName: "lastName",
          userName: "username",
          email: "email",
          role: "role",
        };

        const changedFields = Object.keys(fieldMap).reduce((acc, key) => {
          const originalValue = selectedUser?.[fieldMap[key]] ?? "";
          if (formData[key] !== originalValue) {
            acc[key] = formData[key];
          }
          return acc;
        }, {});

        payload = new PayloadBuilder()
          .setFromObject(changedFields, Object.keys(changedFields))
          .build();
      } else {
        payload = new PayloadBuilder()
          .setFromObject(formData, ["firstName", "lastName", "userName"])
          .setIfExists("role", formData.role)
          .setIfExists("email", formData.email)
          .setIfExists("userName", formData.userName || "")
          .setIfExists("password", formData.password)
          .build();
      }

      const data = isEditing
        ? await updateUser(payload, selectedUser?.userId)
        : await createUser(payload);

      showSuccessMessage(
        data?.message ||
          (isEditing
            ? "User updated successfully!"
            : "User added successfully!"),
      );

      getUserLists();
      handleClose();
    } catch (error) {
      showErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          (isEditing
            ? "Failed to update user!"
            : "Failed to add user. User may already exist!"),
      );
    } finally {
      setLoading(false);
    }
  }, [
    formData,
    isEditing,
    selectedUser,
    validateForm,
    setLoading,
    getUserLists,
    handleClose,
  ]);

  const fields = useMemo(
    () =>
      getUserFormFields(
        isEditing,
        showPassword,
        () => setShowPassword((p) => !p),
        isCurrentUserAdminSupport,
      ),
    [isEditing, showPassword, isCurrentUserAdminSupport],
  );

  return (
    <CustomModal
      visible={isModalOpen}
      onClose={handleClose}
      title={isEditing ? "Edit User" : "Add New User"}
      height={400}
      destroyOnClose
    >
      <div>
        <Form
          fields={fields}
          formData={formData}
          sectionConfig={USER_FORM_SECTION_CONFIG}
          onChange={handleFormChange}
          onBlur={handleBlur}
          onSubmit={handleSubmit}
          errors={errors}
          setErrors={setErrors}
          onCancel={handleClose}
          showClearButton={false}
          showCancelButton={true}
          isSubmitting={loading}
        />
      </div>
    </CustomModal>
  );
};

export default UsersModal;
